import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import utils from '../../utils';
import { GrAdd, GrSearch } from 'react-icons/gr';
import { FiDownload, FiPlusCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import PatientStats from '../../components/patients/PatientStats';
import adminServices from '../../services/AdminServices';
import FetchError from '../../components/feedback/FetchError';
import moment from 'moment';
import Pagination from '../../components/nav/Pagination';
import Empty from '../../components/feedback/Empty';
import PatientModal from '../../components/patients/PatientModal';
import Loading from '../../components/feedback/Loading';

function PatientsListPage(props) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [patients, setPatients] = useState(null);
    const [action, setAction] = useState(0);
    const [activeUser, setActiveUser] = useState(null);

    const searchInputRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    const currentOrdering = searchParams.get('ordering') || '';
    const currentSearch = searchParams.get('search') || '';

    useEffect(() => {
        const params = searchParams.toString();
        getPatients(params);
    }, [searchParams]);

    const getPatients = (params) => {
        setError(null);
        setLoading(true);
        adminServices.getPatients(params ? `?${params}` : '').then(
            res => {
                setPatients(res.data);
                setLoading(false);
            },
            err => {
                console.error(err);
                setError(err);
                setLoading(false);
            }
        );
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            const query = e.target.value.trim();
            setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                if (query) {
                    newParams.set('search', query);
                } else {
                    newParams.delete('search');
                }
                newParams.delete('page'); // reset to first page
                return newParams;
            });
        }
    };

    const updateURL = (newParams) => {
        navigate({
            pathname: location.pathname,
            search: newParams.toString(),
        }, { replace: true });
    };

    const handleOrderingChange = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(searchParams);

        if (value) {
            params.set('ordering', value);
        } else {
            params.delete('ordering');
        }
        params.delete('page');

        updateURL(params);
    };

    const changePageParam = (page) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (page) {
                newParams.set('page', page);
            } else {
                newParams.delete('page');
            }
            return newParams;
        });
    };

    const downloadCsv = () => {
        toast.error("Feature coming soon");
    };

    function openModal(action, user = null) {
        const openBtn = document.getElementById("patient-create-open-btn");
        setAction(action);
        setActiveUser(user);
        if (openBtn) openBtn.click();
    }

    const url = window.location.href;

    return (
        <div className="mb-16">
            <Helmet>
                <title>Patients - {utils.siteName}</title>
                <link rel="canonical" href={url} />
                <meta property="description" content="Manage patient records, care needs, and assignments." />
            </Helmet>

            <PatientModal
                onReload={getPatients}
                setActiveUser={setActiveUser}
                action={action}
                activeUser={activeUser}
            />

            <div className="container mx-auto space-y-5">
                <PatientStats />

                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">Patient Directory</h1>
                        <p className="text-zinc-500 mt-1">Manage patient records, care needs, and assignments.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={downloadCsv}
                            className="items-center hidden justify-center px-4 py-2 bg-white dark:bg-zinc-800 border border-border-light dark:border-zinc-700 rounded-lg text-sm font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm transition-all"
                        >
                            <FiDownload className="inline mr-2 text-[20px]" />
                            Export Record
                        </button>
                        {/* Modal will be added later — placeholder button */}
                        <button
                            onClick={() => openModal(0)}
                            className="inline-flex items-center justify-center px-4 py-2 bg-primary hover:bg-sky-600 border border-transparent rounded-lg text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm transition-all"
                        >
                            <GrAdd className="inline mr-2 text-[20px]" />
                            Add New Patient
                        </button>
                    </div>
                </div>

                <div className="bg-surface-light p-4 rounded-xl border border-border-light">
                    <div className="flex flex-col md:flex-row gap-4 justify-between">
                        <div>
                            <label className="sr-only" htmlFor="patient-search">Search</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <GrSearch className="text-zinc-400 text-[20px]" />
                                </div>
                                <input
                                    className="block w-full pl-10 pr-3 py-2.5 border border-border-light rounded-lg bg-zinc-50 text-zinc-900 placeholder-zinc-500 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                                    id="patient-search"
                                    name="patient-search"
                                    placeholder="Search by name, email or ID"
                                    type="text"
                                    defaultValue={currentSearch}
                                    onKeyDown={handleSearch}
                                    ref={searchInputRef}
                                />
                            </div>
                        </div>

                        <div>
                            <div className="relative">
                                <select
                                    className="block w-full pl-3 pr-10 py-2.5 text-base border-border-light focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-lg bg-zinc-50 text-zinc-700 appearance-none border"
                                    value={currentOrdering}
                                    onChange={handleOrderingChange}
                                >
                                    <option value="">Sort By</option>
                                    <option value="user__full_name">Name (A-Z)</option>
                                    <option value="-user__full_name">Name (Z-A)</option>
                                    <option value="age">Age (Low-High)</option>
                                    <option value="-age">Age (High-Low)</option>
                                    <option value="creation_time">Oldest First</option>
                                    <option value="-creation_time">Newest First</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-zinc-500">
                                    <FiPlusCircle className="text-[20px]" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {error && <FetchError error={error} />}

                {loading ? (
                    <Loading />
                ) : (
                    <div>
                        <div className="bg-surface-light dark:bg-surface-dark rounded-xl border border-border-light dark:border-border-dark overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="table table-compact table-zebra w-full">
                                    <thead>
                                        <tr>
                                            <th>Patient</th>
                                            <th className="hidden md:table-cell">Location</th>
                                            <th>Age</th>
                                            <th className="hidden md:table-cell">Care Needs</th>
                                            <th className="hidden md:table-cell">Nurse Requests</th>
                                            <th className="hidden md:table-cell">Created</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {patients?.results?.length > 0 ? (
                                            patients.results.map((patient) => (
                                                <tr key={patient.id}>
                                                    <td>
                                                        <Link to={`/patient/${patient?.id}`} className="flex gap-3 items-center group hover:cursor-pointer">
                                                            <div className="hidden md:avatar placeholder">
                                                                {patient?.user?.image ? (
                                                                    <div className="w-14 rounded-full">
                                                                        <img src={patient.user.image} alt={patient.user.full_name} />
                                                                    </div>
                                                                ) : (
                                                                    <div className="bg-neutral text-white rounded-full w-14">
                                                                        <span className="text-lg">
                                                                            {utils.makeProfileLetter(patient.user)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div>
                                                                {patient?.user?.full_name && (
                                                                    <p className="font-bold group-hover:underline">{patient.user.full_name}</p>
                                                                )}
                                                                <p className="text-gray-600">
                                                                    {patient?.user?.phone_number ? (
                                                                        <span>{patient?.user?.phone_number}</span>
                                                                    ) : (
                                                                        <span>{patient?.user?.email}</span>
                                                                    )}
                                                                </p>
                                                            </div>
                                                        </Link>
                                                    </td>
                                                    <td className="hidden md:table-cell">
                                                        {patient?.location?.address || patient?.location?.city || '--'}
                                                    </td>
                                                    <td>{patient?.age || '—'}</td>
                                                    <td className="hidden md:table-cell">
                                                        {(() => {
                                                            const conds = patient?.conditions || [];
                                                            if (conds.length === 0) return '—';
                                                            const displayed = conds.slice(0, 2).map(c => c.name);
                                                            return conds.length > 2 ? displayed.join(', ') + ', ...' : displayed.join(', ');
                                                        })()}
                                                    </td>
                                                    <td className="hidden md:table-cell">
                                                        {patient?.request_count}
                                                    </td>
                                                    <td className="hidden md:table-cell">
                                                        {moment(patient?.creation_time).format("MMM Do YYYY")}
                                                    </td>
                                                    <td>
                                                        <button
                                                            onClick={() => openModal(1, patient)}
                                                            className="btn btn-secondary btn-sm"
                                                        >
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : null}
                                    </tbody>
                                </table>
                                {patients?.results?.length === 0 && (
                                    <div className='p-3'>
                                        <Empty objectName="Patients" />
                                    </div>
                                )}
                            </div>
                        </div>

                        {!loading && !error && patients && patients.count > 0 && (
                            <div className="flex justify-center mt-7">
                                <Pagination
                                    count={patients.count}
                                    next={patients.next}
                                    previous={patients.previous}
                                    changePageParam={changePageParam}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default PatientsListPage;